import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import AuthSplitLayout from './AuthSplitLayout';
import { authApi } from './auth-api';
import { mapAuthError } from './auth-error-map';

export default function AuthForgotPassword() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setLoading(true);
        try {
            await authApi.forgotPassword({ email });
            setSent(true);
        } catch (error) {
            const mapped = mapAuthError(error, t, 'forgot-password');
            enqueueSnackbar(mapped.message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthSplitLayout>
            <Stack spacing={3}>
                {/* Back link */}
                <Box>
                    <Typography
                        component={Link}
                        to="/auth/login"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 16 }} />
                        {t('auth.forgot-password.back-to-login')}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.forgot-password.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.forgot-password.subheading')}
                    </Typography>
                </Box>

                {sent ? (
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'success.light',
                            color: 'success.dark',
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body1" fontWeight={600}>
                            ✉️ {t('auth.forgot-password.sent')}
                        </Typography>
                    </Box>
                ) : (
                    <Stack component="form" spacing={2} onSubmit={onSubmit}>
                        <TextField
                            label={t('auth.step.email.label')}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                            autoComplete="email"
                            fullWidth
                        />
                        <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
                            {loading ? t('auth.common.loading') : t('auth.forgot-password.submit')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </AuthSplitLayout>
    );
}
